import axios from "axios";

export const handleApiError = (error) => {
	if (error.response) {
		const { status, data } = error.response;

		if (status === 404 && data?.response?.message?.[0]?.includes("does not exist")) {
			return `Instância não existe: ${data.response.message[0]}`;
		}

		if (status === 400 && data?.response?.message?.[0]?.includes("disconnected")) {
			return `Instância desconectada: ${data.response.message[0]}`;
		}

		if (status === 401) {
			return "Não autorizado: verifique sua API Key.";
		}

		return `Erro ${status}: ${data?.error || "Erro desconhecido."}`;
	}

	if (error.message) {
		return `Erro: ${error.message}`;
	}

	return "Ocorreu um erro desconhecido.";
};

// Dicionário de Erros da API WhatsApp Business
const errorMap = {
	// Authorization / Permissions
	0: "Erro de autenticação. Verifique o token da API.",
	10: "Permissão negada. Verifique as configurações da conta.",
	190: "Token de acesso expirado. Reconecte a instância.",
	1005: "Acesso negado. Verifique permissões ou se a conta é Cloud API.",

	// Throttling / Limits
	4: "Muitas requisições (API Rate Limit). Aguarde um momento.",
	130429: "Limite de envio atingido. Reduza a velocidade de disparo.",
	131048: "Número bloqueado por spam ou limite de qualidade atingido.",
	131056: "Limite de taxa atingido (Par Business/Consumer).",
	80007: "Taxa de limite de envio excedida (Rate Limit).",

	// Validation / Integrity / Parameters
	100: "Parâmetro inválido ou inexistente na requisição.",
	131008: "Parâmetro obrigatório faltando na requisição.",
	131009: "Valor de parâmetro incorreto ou inválido.",
	131051: "Tipo de mensagem não suportado.",
	368: "Bloqueado temporariamente por violação de políticas.",
	131031: "Conta abusiva. Bloqueada temporariamente.",

	// Message Delivery
	131000: "Falha genérica no envio. Tente novamente.",
	131026: "Número não possui WhatsApp ou versão incompatível.",
	131047: "Janela de 24h fechada. Use um template aprovado.",
	131049: "Bloqueado para proteção do ecossistema (Spam/Qualidade).",
	131052: "Erro ao baixar mídia do cabeçalho.",
	131053: "Erro ao fazer upload da mídia.",
	131057: "Falha ao enviar mensagem (User Unavailable).",

	// Templates
	132000: "Número de parâmetros incorreto verifique o template.",
	132001: "Template não existe ou ainda não foi aprovado.",
	132005: "Template não suporta o idioma solicitado.",
	132007: "Tag do template inválida ou desconhecida.",
	132012: "Template rejeitado pela Meta.",
	132015: "Template ainda em análise (Pending).",
	132016: "Template pausado (baixa qualidade).",
	132018: "Erro nos parâmetros do template, verifique o template.",

	// Billing / Account
	131042: "Erro de pagamento ou elegibilidade da conta Business.",
	133016: "Limite de registro/desregistro atingido.",
	133010: "Conta desativada ou banida.",
};

const translateError = (errorData) => {
	let code = errorData?.code || errorData?.error_code || errorData?.error?.code;
	const type = errorData?.type || errorData?.error?.type;
	const message =
		errorData?.message ||
		errorData?.error?.message ||
		(typeof errorData === "string" ? errorData : JSON.stringify(errorData));
	const details = errorData?.error_data?.details || errorData?.details || "";

	// Se o erro estiver dentro de um objeto 'error' (padrão Graph API às vezes)
	if (!code && errorData?.error) {
		code = errorData.error.code;
	}

	let translated = "";

	// Tenta capturar pelo código numérico
	if (code && errorMap[code]) {
		translated = errorMap[code];
	}
	// Tenta capturar por strings conhecidas
	else if (String(type).includes("OAuthException") || String(message).includes("OAuthException")) {
		translated = "Erro de Autenticação/Permissão (OAuthException).";
	} else if (String(message).includes("does not exist")) {
		translated = "Template não encontrado ou não aprovado.";
	} else if (String(message).includes("parameters")) {
		translated = "Erro nos parâmetros do template.";
	}

	// Monta o objeto de retorno traduzido
	const errorObj = {
		message: translated || message,
		code: code,
		details: details,
		original: message,
	};

	if (translated) {
		// Formato de texto amigável para exibição direta
		// Retorna: Tradução (Cód: X) \n Detalhes: ...
		errorObj.friendlyMessage = `${translated} ${code ? `(Cód: ${code})` : ""}${details ? `\nDetalhes: ${details}` : `\nOrigem: ${message}`}`;
	} else {
		errorObj.friendlyMessage = typeof errorData === "string" ? errorData : JSON.stringify(errorData, null, 2);
	}

	return errorObj;
};

// 1️⃣ Enviar Template (Evolution API)

// 1️⃣ Enviar Template (Evolution API)
export const sendEvolutionTemplate = async (
	url,
	apiKey,
	instance,
	to,
	templateName,
	language = "pt_BR",
	variables = [],
	imageUrl = null, // Se o template tiver header de imagem
) => {
	if (!url || !apiKey || !instance) {
		throw new Error("Credenciais da Instância (Evolution) não fornecidas.");
	}

	// Normalizar telefone (somente números)
	const phoneStr = `${to}`.replace(/\D/g, "");
	// Evolution geralmente aceita com ou sem 55, mas ideal é DDI + DDD + número.
	const dest = phoneStr.startsWith("55") || phoneStr.length > 11 ? phoneStr : `55${phoneStr}`;

	const components = [];

	// 1. Header (Imagem)
	if (imageUrl) {
		components.push({
			type: "header",
			parameters: [
				{
					type: "image",
					image: {
						link: imageUrl,
					},
				},
			],
		});
	}

	// 2. Body (Variáveis)
	if (Array.isArray(variables) && variables.length > 0) {
		components.push({
			type: "body",
			parameters: variables.map((val) => ({
				type: "text",
				text: String(val ?? ""),
			})),
		});
	}

	// Payload conforme solicitado pelo usuário
	const payload = {
		number: dest,
		name: templateName,
		language: language,
		components: components,
	};

	const endpoint = `https://${url}/message/sendTemplate/${instance}`;

	try {
		const { data } = await axios.post(endpoint, payload, {
			headers: {
				apikey: apiKey,
				"Content-Type": "application/json",
			},
		});

		// Check for functional errors (200 OK but with error payload)
		if (data && (data.type === "OAuthException" || data.error || (data.code && data.code > 299))) {
			const customError = new Error("Evolution API Functional Error");
			customError.response = { data };
			throw customError;
		}

		return data;
	} catch (error) {
		console.error("Erro Evolution API:", error.response?.data || error.message);

		let errorData = error.response?.data;

		// Tratamento especial para erro funcional lançado manualmente
		if (error.message === "Evolution API Functional Error" && error.response?.data) {
			errorData = error.response.data;
		}

		if (!errorData) errorData = error.message;

		// Traduz e recebe objeto com metadados
		const translatedObj = translateError(errorData);

		// Lança erro com propriedades adicionais para ser tratado no loop
		const finalError = new Error(translatedObj.friendlyMessage);
		finalError.code = translatedObj.code;
		finalError.details = translatedObj.details;
		throw finalError;
	}
};

// 2️⃣ Verificar status da instância
export const getStatus = async (url, apiKey, instance) => {
	try {
		const response = await axios.get(`https://${url}/instance/connectionState/${instance}`, {
			headers: {
				apikey: apiKey,
				"Content-Type": "application/json",
			},
		});

		return response.data;
	} catch (error) {
		const friendlyMessage = handleApiError(error);
		console.error("Erro ao verificar status:", friendlyMessage);
		throw new Error(friendlyMessage);
	}
};
// 3️⃣ Buscar Templates (Evolution API)
export const fetchTemplates = async (url, apiKey, instance) => {
	try {
		// Tenta o endpoint padrão da Evolution
		const response = await axios.get(`https://${url}/template/fetch/${instance}`, {
			headers: {
				apikey: apiKey,
				"Content-Type": "application/json",
			},
		});

		// A Evolution geralmente retorna { status: 200, data: [...] } ou diretamente o array
		return response.data;
	} catch (error) {
		const friendlyMessage = handleApiError(error);
		console.error("Erro ao buscar templates:", friendlyMessage);
		throw new Error(friendlyMessage);
	}
};
